import { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { Button } from '@mui/material';
import { Iconify } from 'src/components/iconify';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { downloadBlob } from 'src/utils/format';
import { exportWithdrawals } from '../../../services/xbitRelayWithdrawalsService';
import {
  extractBlobErrorMessage,
  fallbackExportFilename,
  parseFilenameFromDisposition,
} from '../../../services/xbitRelayExport.helpers';
import { ExportCsvDialog, ExportCsvFilter } from '../ExportCsvDialog';
import {
  WITHDRAWAL_PROVIDERS,
  XBIT_RELAY_WITHDRAWALS_EXPORT_ACTION,
  XBIT_RELAY_WITHDRAWALS_PERMISSION_RESOURCE,
} from './constants';

const I18N_BASE = 'module.xbitRelayWithdrawals';
const PROVIDERS = [...WITHDRAWAL_PROVIDERS];

export const ExportButton = () => {
  const t = useTranslate();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleExport = async (filter: ExportCsvFilter) => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await exportWithdrawals(filter);
      const filename =
        parseFilenameFromDisposition(null) ?? fallbackExportFilename('relay-withdrawals', filter);
      downloadBlob(filename, blob);
      setOpen(false);
    } catch (error: any) {
      const message = await extractBlobErrorMessage(error?.response?.data);
      notify(message ?? 'ra.notification.http_error', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PermissionGuard
      resource={XBIT_RELAY_WITHDRAWALS_PERMISSION_RESOURCE}
      action={XBIT_RELAY_WITHDRAWALS_EXPORT_ACTION}
      fallback={<></>}
    >
      <Button
        variant="contained"
        color="primary"
        onClick={() => setOpen(true)}
        startIcon={<Iconify icon="solar:export-bold" />}
        sx={{ height: 40, px: 2, whiteSpace: 'nowrap', textTransform: 'none', boxShadow: 'none' }}
      >
        {t(`${I18N_BASE}.export`)}
      </Button>
      <ExportCsvDialog
        open={open}
        busy={busy}
        i18nBase={I18N_BASE}
        providers={PROVIDERS}
        onClose={() => setOpen(false)}
        onExport={handleExport}
      />
    </PermissionGuard>
  );
};
